import { useState, useRef, useMemo, useCallback, useEffect, type SetStateAction } from "react";
import type * as type from "@vTypes";
import type { SaveWeeklyOffNursePayload } from "@vTypes/weeklyOff";
import type { Weekday } from "@utils/dayUtill";

type DayOfWeek =
    | "총 배치인원"
    | "월요일"
    | "화요일"
    | "수요일"
    | "목요일"
    | "금요일"
    | "토요일"
    | "일요일";

type ActualDayOfWeek = Exclude<DayOfWeek, "총 배치인원">;

const ALL_ACTUAL_DAYS: ActualDayOfWeek[] = [
    "월요일",
    "화요일",
    "수요일",
    "목요일",
    "금요일",
    "토요일",
    "일요일",
];

export function useWeeklyOffManagement(
    nurses: type.FullNurseSchema[] | undefined
) {
    // 초기화 ref
    const isInitializedRef = useRef(false);

    // 각 요일별 '주휴배치' 간호사 목록을 관리하는 상태
    const [assignedNursesByDay, setAssignedNursesByDay] = useState<
        Record<ActualDayOfWeek, type.FullNurseSchema[]>
    >({} as Record<ActualDayOfWeek, type.FullNurseSchema[]>);

    // 미지정 간호사의 순서를 관리하는 상태
    const [unassignedNursesOrder, setUnassignedNursesOrder] = useState<string[]>([]);

    // 초기 배치 상태를 저장 (변경사항 추적용)
    const initialAssignmentsRef = useRef<Record<ActualDayOfWeek, string[]>>({
        월요일: [],
        화요일: [],
        수요일: [],
        목요일: [],
        금요일: [],
        토요일: [],
        일요일: [],
    });

    // 요일을 숫자로 변환하는 헬퍼 함수
    const dayToWeekday = useCallback((day: ActualDayOfWeek): number => {
        const dayMap: Record<ActualDayOfWeek, number> = {
            월요일: 0,
            화요일: 1,
            수요일: 2,
            목요일: 3,
            금요일: 4,
            토요일: 5,
            일요일: 6,
        };
        return dayMap[day];
    }, []);

    // '미지정' 간호사 목록을 useMemo로 계산 (순서 반영)
    const unassignedNurses = useMemo(() => {
        if (!nurses) return [];
        const allAssignedNurseIds = new Set(
            Object.values(assignedNursesByDay)
                .flat()
                .map((n) => n.nurse_id)
        );
        const unassigned = nurses.filter(
            (n) => !allAssignedNurseIds.has(n.nurse_id)
        );

        // 순서가 저장되어 있으면 그 순서대로 정렬
        if (unassignedNursesOrder.length > 0) {
            return unassignedNursesOrder
                .map((id) => unassigned.find((n) => n.nurse_id === id))
                .filter((n): n is type.FullNurseSchema => n !== undefined);
        }

        return unassigned;
    }, [nurses, assignedNursesByDay, unassignedNursesOrder]);

    // '미지정' 목록을 업데이트하는 함수
    const setUnassignedNurses = useCallback(
        (value: SetStateAction<type.FullNurseSchema[]>) => {
            const newList =
                typeof value === "function" ? value(unassignedNurses) : value;
            setUnassignedNursesOrder(newList.map((n) => n.nurse_id));
        },
        [unassignedNurses]
    );

    // 각 간호사가 어느 요일에 배치되었는지 찾는 헬퍼 함수
    const getAssignedDayForNurse = useCallback(
        (nurseId: string): string | undefined => {
            for (const [day, nurseList] of Object.entries(assignedNursesByDay)) {
                if (nurseList.some((n) => n.nurse_id === nurseId)) {
                    return day;
                }
            }
            return undefined;
        },
        [assignedNursesByDay]
    );

    // ModalWeekOffFullNurse에 실시간 주휴일 정보를 전달하기 위한 데이터 가공
    const nursesWithCurrentWeeklyOff = useMemo(() => {
        if (!nurses) return [];

        const assignedDayMap = new Map<string, number>();
        Object.entries(assignedNursesByDay).forEach(([day, nurseList]) => {
            const weekday = dayToWeekday(day as ActualDayOfWeek);
            nurseList.forEach(nurse => {
                assignedDayMap.set(nurse.nurse_id, weekday);
            });
        });

        return nurses.map(nurse => {
            const assignedWeekday = assignedDayMap.get(nurse.nurse_id);
            return {
                ...nurse,
                weekly_off_weekday: assignedWeekday !== undefined ? assignedWeekday : null,
            };
        });
    }, [nurses, assignedNursesByDay, dayToWeekday]);

    // 변경사항을 계산하는 함수 (useEffect에서 사용하기 위해 위로 이동)
    const calculateChanges = useCallback((): SaveWeeklyOffNursePayload[] => {
        const changes: SaveWeeklyOffNursePayload[] = [];
        const currentAssignedIds = new Set<string>();

        Object.entries(assignedNursesByDay).forEach(([day, nurseList]) => {
            const dayName = day as ActualDayOfWeek;
            const weekday = dayToWeekday(dayName);

            nurseList.forEach((nurse) => {
                currentAssignedIds.add(nurse.nurse_id);

                if (!initialAssignmentsRef.current[dayName].includes(nurse.nurse_id)) {
                    changes.push({
                        nurse_id: nurse.nurse_id,
                        weekly_off_enabled: true,
                        weekly_off_weekday: weekday as Weekday,
                    });
                } else {
                    const initialDay = Object.entries(initialAssignmentsRef.current).find(
                        ([_day, ids]) => ids.includes(nurse.nurse_id)
                    )?.[0] as ActualDayOfWeek | undefined;

                    if (initialDay && initialDay !== dayName) {
                        changes.push({
                            nurse_id: nurse.nurse_id,
                            weekly_off_enabled: true,
                            weekly_off_weekday: weekday as Weekday,
                        });
                    }
                }
            });
        });

        Object.entries(initialAssignmentsRef.current).forEach(([_day, nurseIds]) => {
            nurseIds.forEach((nurseId) => {
                if (!currentAssignedIds.has(nurseId)) {
                    changes.push({
                        nurse_id: nurseId,
                        weekly_off_enabled: false,
                        weekly_off_weekday: null as any,
                    });
                }
            });
        });

        return changes;
    }, [assignedNursesByDay, dayToWeekday]);

    // 간호사 배치 변경사항 확인 함수
    const hasNurseChanges = useCallback((): boolean => {
        const changes = calculateChanges();
        return changes.length > 0;
    }, [calculateChanges]);

    // 초기 배치 설정
    useEffect(() => {
        if (!nurses) {
            return;
        }

        // 이미 초기화되었고, 변경사항(작업중인 내용)이 있다면 서버 데이터로 덮어쓰지 않음 (작업 보호)
        if (isInitializedRef.current && hasNurseChanges()) {
            return;
        }

        const initialAssignments: Record<ActualDayOfWeek, type.FullNurseSchema[]> = {
            월요일: [],
            화요일: [],
            수요일: [],
            목요일: [],
            금요일: [],
            토요일: [],
            일요일: [],
        };

        const initialIds: Record<ActualDayOfWeek, string[]> = {
            월요일: [],
            화요일: [],
            수요일: [],
            목요일: [],
            금요일: [],
            토요일: [],
            일요일: [],
        };

        nurses.forEach((weeklyOffNurse) => {
            const dayIndex = weeklyOffNurse.weekly_off_weekday;
            const dayName = ALL_ACTUAL_DAYS[dayIndex as Weekday];
            const nurse = nurses.find((n) => n.nurse_id === weeklyOffNurse.nurse_id);
            if (nurse && weeklyOffNurse.weekly_off_weekday != null && dayName) {
                initialAssignments[dayName].push(nurse);
                initialIds[dayName].push(nurse.nurse_id);
            }
        });

        initialAssignmentsRef.current = initialIds;
        setAssignedNursesByDay(initialAssignments);
        isInitializedRef.current = true;
    }, [nurses]); // eslint-disable-line react-hooks/exhaustive-deps

    // 상태를 초기값으로 되돌리는 함수
    const resetAssignments = useCallback(() => {
        if (!nurses) return;

        const initialAssignments: Record<ActualDayOfWeek, type.FullNurseSchema[]> = {
            월요일: [], 화요일: [], 수요일: [], 목요일: [], 금요일: [], 토요일: [], 일요일: [],
        };

        const initialIds: Record<ActualDayOfWeek, string[]> = {
            월요일: [], 화요일: [], 수요일: [], 목요일: [], 금요일: [], 토요일: [], 일요일: [],
        };

        nurses.forEach((nurse) => {
            const dayIndex = nurse.weekly_off_weekday;
            const dayName = ALL_ACTUAL_DAYS[dayIndex as Weekday];
            if (nurse && nurse.weekly_off_weekday != null && dayName) {
                initialAssignments[dayName].push(nurse);
                initialIds[dayName].push(nurse.nurse_id);
            }
        });

        initialAssignmentsRef.current = initialIds;
        setAssignedNursesByDay(initialAssignments);
    }, [nurses]);

    // 프리셉터 정보를 가져오는 헬퍼 함수 (linkPreceptorRelations 매핑 시 preceptor 객체 우선)
    const getPreceptorInfo = useCallback(
        (nurse: type.FullNurseSchema): string => {
            if (!nurses) return "-";

            const withRelation = nurse as type.FullNurseSchema & {
                preceptor?: { name: string } | null;
            };
            if (withRelation.preceptor?.name) {
                return withRelation.preceptor.name;
            }

            if (nurse.preceptor_id) {
                const preceptor = nurses.find((n) => n.nurse_id === nurse.preceptor_id);
                return preceptor ? preceptor.name : "없음";
            }

            const isPreceptor = nurses.some((n) => n.preceptor_id === nurse.nurse_id);
            if (isPreceptor) {
                return "프리셉터";
            }

            return "-";
        },
        [nurses]
    );

    // 초기 배치 상태 업데이트 (저장 후)
    const updateInitialAssignments = useCallback(() => {
        const newInitialIds: Record<ActualDayOfWeek, string[]> = {
            월요일: [],
            화요일: [],
            수요일: [],
            목요일: [],
            금요일: [],
            토요일: [],
            일요일: [],
        };
        Object.entries(assignedNursesByDay).forEach(([day, nurseList]) => {
            newInitialIds[day as ActualDayOfWeek] = nurseList.map((n) => n.nurse_id);
        });
        initialAssignmentsRef.current = newInitialIds;
    }, [assignedNursesByDay]);

    return {
        assignedNursesByDay,
        unassignedNurses,
        nursesWithCurrentWeeklyOff,

        // 상태 설정 함수
        setAssignedNursesByDay,
        setUnassignedNurses,

        // 헬퍼 함수
        getAssignedDayForNurse,
        getPreceptorInfo,
        dayToWeekday,

        // 변경사항 관련
        calculateChanges,
        hasNurseChanges,
        updateInitialAssignments,
        resetAssignments,

    };
}